import Cookie from 'js-cookie';

import { publicEnv } from '~/features/environment/public';
import { logger } from '~/features/logging/logger';

import { SEGMENT_SESSION_ID_COOKIE } from '../constants';

import { getSessionReplaySampleRate } from './get-session-replay-sample-rate';
import { isSessionReplayAllowedForCountry } from './session-replay-geo';

// Type-only import keeps the runtime SDK out of the shared bundle — it's
// loaded via dynamic import inside `initSessionReplay` only after geo + env
// gates pass. Bundle analyzer (CONENG-2258): static import pulls ~32KB gzipped
// of `@amplitude/session-replay-browser` into every instrumented route. Lazy
// loading keeps the SDK in its own chunk that downloads only when init
// actually runs (sampled, allowed-geo users).
type SessionReplayModule = typeof import('@amplitude/session-replay-browser');

const FILE = 'src/features/session-replay/utils/init-session-replay.ts';

let initPromise: Promise<void> | null = null;
let middlewareRegistered = false;
let destinationMiddlewareRegistered = false;
let cachedSessionId: string | null = null;
let sessionReplayModulePromise: Promise<SessionReplayModule> | null = null;
/** Resolves after `sessionReplay.init()` succeeds; kept across middleware retry. */
let sessionReplaySdkReady: Promise<SessionReplayModule> | null = null;

function loadSessionReplayModule(): Promise<SessionReplayModule> {
  if (!sessionReplayModulePromise) {
    sessionReplayModulePromise = import('@amplitude/session-replay-browser');
  }
  return sessionReplayModulePromise;
}

async function initializeSessionReplaySdk({
  apiKey,
  deviceId,
  sessionId,
}: {
  apiKey: string;
  deviceId: string | undefined;
  sessionId: number;
}): Promise<SessionReplayModule> {
  if (sessionReplaySdkReady) {
    return sessionReplaySdkReady;
  }

  sessionReplaySdkReady = (async () => {
    const sessionReplay = await loadSessionReplayModule();
    await sessionReplay.init(apiKey, {
      deviceId,
      sessionId,
      sampleRate: getSessionReplaySampleRate(),
      privacyConfig: {
        defaultMaskLevel: 'medium',
      },
    }).promise;
    return sessionReplay;
  })();

  try {
    return await sessionReplaySdkReady;
  } catch (error) {
    sessionReplaySdkReady = null;
    throw error;
  }
}

function resolveInitialSessionId({
  ampSessionId,
  sessionIdCookie,
}: {
  ampSessionId: number | null;
  sessionIdCookie: string | undefined;
}): number {
  // Amplitude session ids are millis timestamps; 0 means "no session yet", not a
  // settled id — prefer `analytics_session_id` when Amplitude hasn't advanced.
  const hasSettledAmpSession = typeof ampSessionId === 'number' && !Number.isNaN(ampSessionId) && ampSessionId > 0;
  if (hasSettledAmpSession) {
    return ampSessionId;
  }

  if (sessionIdCookie) {
    const parsed = Number(sessionIdCookie);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
}

type InitArgs = {
  countryCode: string | null | undefined;
};

/**
 * Initialize Amplitude Session Replay once per browser session, gated by:
 *   1. `NEXT_PUBLIC_AMPLITUDE_API_KEY` set to a real key (kill switch when
 *      empty/undefined OR the literal string "false" — used on envs like
 *      test/mock/staging where we explicitly do not record).
 *   2. Country code allowed (fail closed for unknown — see
 *      `isSessionReplayAllowedForCountry`).
 *   3. Segment `window.analytics` available (we run as a destination on top
 *      of the existing Segment + Amplitude Classic device-mode setup).
 *
 * The function is idempotent across route changes and Fast Refresh — repeated
 * calls return the same in-flight / resolved promise.
 *
 * Pages opt in via `useSessionReplay()`; do not call this directly from
 * `_app.tsx` (we want page-level scope).
 */
export function initSessionReplay({ countryCode }: InitArgs): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();

  const rawApiKey = publicEnv.NEXT_PUBLIC_AMPLITUDE_API_KEY;
  const apiKey = rawApiKey?.trim() ?? '';
  const isDisabled = !apiKey || apiKey.toLowerCase() === 'false';

  if (isDisabled) {
    if (publicEnv.NEXT_PUBLIC_DEBUG_ANALYTICS) {
      logger.debug('Session Replay skipped: NEXT_PUBLIC_AMPLITUDE_API_KEY is empty or set to "false"', {
        file: FILE,
        function: 'initSessionReplay',
      });
    }
    return Promise.resolve();
  }

  if (!isSessionReplayAllowedForCountry(countryCode)) {
    if (publicEnv.NEXT_PUBLIC_DEBUG_ANALYTICS) {
      logger.debug('Session Replay skipped: country code not allowed', {
        file: FILE,
        function: 'initSessionReplay',
        countryCode: countryCode ?? null,
      });
    }
    return Promise.resolve();
  }

  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const ready = await waitForAnalytics();
      if (!ready || !window.analytics) {
        if (publicEnv.NEXT_PUBLIC_DEBUG_ANALYTICS) {
          logger.debug('Session Replay skipped: Segment analytics never became ready', {
            file: FILE,
            function: 'initSessionReplay',
          });
        }
        initPromise = null;
        return;
      }

      // Follow the Amplitude Segment + Classic device-mode integration doc:
      // wait for the Amplitude instance's onInit so we read the deviceId and
      // sessionId Amplitude actually settled on, instead of racing it. If
      // Amplitude never loads (destination blocked / network failure), fall
      // back to Segment's anonymousId + the analytics_session_id cookie so we
      // still capture replays for the allowed cohort.
      const ampInstance = await waitForAmplitudeInstance();

      const sessionIdCookie = Cookie.get(SEGMENT_SESSION_ID_COOKIE);
      const ampSessionId =
        ampInstance && typeof ampInstance.getSessionId === 'function' ? ampInstance.getSessionId() : null;
      // Resolve the session id in priority order: Amplitude's settled (>0)
      // timestamp first, then the analytics_session_id cookie, then 0. Guard against a non-numeric
      // cookie producing NaN — passing NaN to `sessionReplay.init({ sessionId })`
      // silently breaks event-to-replay correlation in Amplitude. Falling
      // back to 0 matches the Amplitude Segment Classic device-mode doc; the
      // SDK treats 0 as "no session yet" and the source middleware will sync
      // the real id on the next Segment event.
      const sessionIdNumber = resolveInitialSessionId({ ampSessionId, sessionIdCookie });
      cachedSessionId = sessionIdCookie ?? (sessionIdNumber ? String(sessionIdNumber) : null);

      // Reuse Amplitude's own deviceId when available — that's the id Segment's
      // Amplitude Classic destination is forwarding events under, so replays
      // line up. Fallback to Segment's anonymousId (which Segment hands to the
      // destination as deviceId) when the Amplitude instance is unreachable.
      const deviceId =
        (ampInstance && ampInstance.options && typeof ampInstance.options.deviceId === 'string'
          ? ampInstance.options.deviceId
          : undefined) ?? readAnonymousId();

      const sessionReplay = await initializeSessionReplaySdk({
        apiKey,
        deviceId,
        sessionId: sessionIdNumber,
      });

      registerSessionIdMiddleware(sessionReplay.setSessionId);
      registerReplayPropertiesMiddleware(sessionReplay.getSessionReplayProperties);
    } catch (error) {
      logger.error('Session Replay init failed', {
        file: FILE,
        function: 'initSessionReplay',
        error,
      });
      // Allow middleware registration to retry without re-running
      // `sessionReplay.init()` — duplicate init can re-fetch config APIs.
      initPromise = null;
    }
  })();

  return initPromise;
}

/**
 * Source middleware factory exported for unit tests. The middleware reads the
 * current Segment `analytics_session_id` cookie on every event, and if it
 * differs from the previously seen value, notifies Session Replay via
 * `setSessionId`. It always calls `next(payload)` so it never blocks the
 * pipeline. The cookie snapshot is cached only after a successful numeric
 * `setSessionId` so a transient throw can retry on the following event.
 */
export function createSessionIdSyncMiddleware({
  setSessionId,
  readCookie = () => Cookie.get(SEGMENT_SESSION_ID_COOKIE) ?? null,
  getCachedSessionId = () => cachedSessionId,
  setCachedSessionId = (next: string | null) => {
    cachedSessionId = next;
  },
}: {
  setSessionId: (sessionId: number) => void;
  readCookie?: () => string | null;
  getCachedSessionId?: () => string | null;
  setCachedSessionId?: (next: string | null) => void;
}) {
  return function sessionIdSyncMiddleware({ payload, next }: { payload: unknown; next: (payload: unknown) => void }) {
    try {
      const nextSessionId = readCookie();
      const previousSessionId = getCachedSessionId();
      if (nextSessionId && nextSessionId !== previousSessionId) {
        const numeric = Number(nextSessionId);
        if (!Number.isNaN(numeric)) {
          setSessionId(numeric);
          setCachedSessionId(nextSessionId);
        }
      }
    } catch (error) {
      logger.warn('Session Replay session-id sync middleware failed', {
        file: FILE,
        function: 'createSessionIdSyncMiddleware',
        error,
      });
    }
    next(payload);
  };
}

/**
 * Destination middleware factory exported for unit tests. Enriches every
 * event routed to the Amplitude (Classic) destination with the current
 * Session Replay properties (`[Amplitude] Session Replay ID`, etc.) so the
 * Amplitude UI can link captured replay blobs back to events.
 *
 * Without this, replays upload to Amplitude but never surface in the UI
 * because no event carries the replay id.
 */
export function createReplayPropertiesMiddleware({
  getSessionReplayProperties,
}: {
  getSessionReplayProperties: () => Record<string, unknown>;
}) {
  return function replayPropertiesMiddleware({
    payload,
    next,
  }: {
    payload: {
      obj?: {
        properties?: Record<string, unknown>;
        context?: { integrations?: Record<string, unknown> };
      };
    };
    next: (payload: unknown) => void;
  }) {
    try {
      const props = getSessionReplayProperties() ?? {};
      if (payload?.obj && Object.keys(props).length > 0) {
        payload.obj.properties = { ...(payload.obj.properties ?? {}), ...props };
      }
    } catch (error) {
      logger.warn('Session Replay destination middleware failed', {
        file: FILE,
        function: 'createReplayPropertiesMiddleware',
        error,
      });
    }
    next(payload);
  };
}

function registerReplayPropertiesMiddleware(getSessionReplayProperties: () => Record<string, unknown>) {
  if (destinationMiddlewareRegistered) return;
  if (typeof window === 'undefined' || !window.analytics) return;

  const analytics = window.analytics as unknown as {
    addDestinationMiddleware?: (
      integration: string,
      middlewares: Array<ReturnType<typeof createReplayPropertiesMiddleware>>
    ) => void;
  };
  if (typeof analytics.addDestinationMiddleware !== 'function') return;

  analytics.addDestinationMiddleware('Amplitude', [createReplayPropertiesMiddleware({ getSessionReplayProperties })]);
  destinationMiddlewareRegistered = true;
}

function registerSessionIdMiddleware(setSessionId: (sessionId: number) => void) {
  if (middlewareRegistered) return;
  if (typeof window === 'undefined' || !window.analytics) return;

  // Segment's `analytics-next` types omit `addSourceMiddleware` (it's the
  // Analytics.js v1 API surface we still use via the classic snippet). Cast
  // locally instead of widening the global Window type.
  const analytics = window.analytics as unknown as {
    addSourceMiddleware?: (fn: ReturnType<typeof createSessionIdSyncMiddleware>) => void;
  };
  if (typeof analytics.addSourceMiddleware !== 'function') return;

  analytics.addSourceMiddleware(createSessionIdSyncMiddleware({ setSessionId }));
  middlewareRegistered = true;
}

type AmplitudeInstance = {
  options?: { deviceId?: string };
  getSessionId?: () => number;
  onInit?: (cb: () => void) => void;
};

/**
 * Resolve the Segment-loaded Amplitude Classic instance via its `onInit`
 * callback per the Amplitude Segment integration doc. Resolves with `null` if
 * the instance never appears within the timeout — callers must handle that
 * fallback rather than block init forever.
 */
function waitForAmplitudeInstance(): Promise<AmplitudeInstance | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);

  const amplitudeGlobal = (window as unknown as { amplitude?: { getInstance?: () => AmplitudeInstance } }).amplitude;
  const instance =
    amplitudeGlobal && typeof amplitudeGlobal.getInstance === 'function' ? amplitudeGlobal.getInstance() : null;
  if (!instance) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: AmplitudeInstance | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    try {
      if (typeof instance.onInit === 'function') {
        instance.onInit(() => finish(instance));
      } else {
        finish(instance);
        return;
      }
    } catch {
      finish(null);
      return;
    }

    setTimeout(() => finish(instance), 3000);
  });
}

function waitForAnalytics(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (!window.analytics) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ready);
    };

    try {
      window.analytics.ready(() => finish(true));
    } catch {
      finish(false);
      return;
    }

    setTimeout(() => finish(false), 5000);
  });
}

function readAnonymousId(): string | undefined {
  if (typeof window === 'undefined' || !window.analytics) return undefined;
  try {
    const user = typeof window.analytics.user === 'function' ? window.analytics.user() : null;
    const anonymousId = user && typeof user.anonymousId === 'function' ? user.anonymousId() : undefined;
    return typeof anonymousId === 'string' ? anonymousId : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Test-only reset. Vitest only — production code must not call this.
 */
export function __resetSessionReplayForTests() {
  initPromise = null;
  middlewareRegistered = false;
  destinationMiddlewareRegistered = false;
  cachedSessionId = null;
  sessionReplayModulePromise = null;
  sessionReplaySdkReady = null;
}
